import { useState, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { useMasterclass } from '@/contexts/MasterclassContext';
import { useTranslations } from '@/hooks/use-translations';
import { useIsAccreditationUser } from '@/hooks/useIsAccreditationUser';
import { cn } from '@/lib/utils';
import { CertificationButton } from '@/modules/certification/components/CertificationButton';
import {
  ReadOnlyToggle,
  ReadOnlyCaseRenderer,
  ReadOnlyCertification,
  CertificationResetButton,
  useReadOnlyMode,
} from '@/modules/read-only';
import { parseHtml } from '@/utils/html-parser';
import { SlideItemType, SlideType } from '@/utils/types/enums';

import { useCaseDetails } from '../hooks/useCaseDetails';
import { CaseCardProps } from '../types';

export const CaseCard = ({
  caseId,
  title,
  configUrl,
  assetBaseUrl,
  isCompleted,
  isStarted,
  progress,
  userId,
  onSelect,
  onStartCertification,
  config,
  isLoadingOverride,
}: CaseCardProps) => {
  const { t } = useTranslations();
  const { isDemo } = useMasterclass();
  const { isReadOnly, hasAccessToReadOnlyMode } = useReadOnlyMode();
  const isAccreditationUser = useIsAccreditationUser();
  const { details, loading, error } = useCaseDetails(configUrl);

  // State for read-only modals
  const [showReadOnlyCase, setShowReadOnlyCase] = useState(false);
  const [showReadOnlyCertification, setShowReadOnlyCertification] = useState(false);

  const anamneseItem = useMemo<any>(() => {
    if (!details || loading) return null;
    return details.slides
      .find((slide: any) => slide.type === SlideType.ANAMNESE)!
      .items.find((el: any) => el.type === SlideItemType.ANAMNESE)! as any;
  }, [details, loading]);

  const isLoading = isLoadingOverride || loading || !anamneseItem;

  const getButtonLabel = () => {
    if (isCompleted) return config.caseCard.statusLabels.completed;
    if (isStarted) return config.caseCard.statusLabels.started;
    return config.caseCard.statusLabels.notStarted;
  };

  const getStatusClass = () => {
    if (isCompleted) return config.colors.statusCompleted;
    if (isStarted) return config.colors.statusStarted;
    return config.colors.statusStarted;
  };

  const handleCardClick = () => {
    if (isLoading) return;

    if (isReadOnly) {
      setShowReadOnlyCase(true);
    } else {
      onSelect(caseId);
    }
  };

  const handleCertificationClick = () => {
    if (isReadOnly || isAccreditationUser) {
      setShowReadOnlyCertification(true);
    } else {
      onStartCertification(caseId);
    }
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        className={cn(
          'cursor-pointer rounded-xl border border-border bg-card p-6',
          config.caseCard.enableHoverEffect && 'transition-all hover:border-accent hover:shadow-lg',
          'group relative flex flex-col overflow-hidden text-left'
        )}
      >
        {/* Status Badge - Top right */}
        {config.caseCard.showStatus && (
          <div className="absolute right-4 top-4">
            <span className={cn('rounded-full px-3 py-1.5 text-xs font-medium', getStatusClass())}>
              {getButtonLabel()}
            </span>
          </div>
        )}

        <div className="mb-4 flex items-center gap-3">
          {details?.clientLogoPath ? (
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-white">
              <img
                src={`${assetBaseUrl}${details.clientLogoPath}`}
                alt="Client logo"
                className="h-full w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10" />
          )}
          <div className="flex-1 pr-24">
            <h3 className="font-bold text-card-foreground transition-colors group-hover:text-accent">
              {title}
            </h3>
            {(() => {
              if (isLoading) {
                return <div className="mt-1 h-4 w-3/4 animate-pulse rounded bg-muted" />;
              }
              if (error) {
                return <p className="mt-1 text-xs text-destructive">Error loading details</p>;
              }
              if (details) {
                return (
                  <p className="text-sm text-muted-foreground">{parseHtml(anamneseItem.title)}</p>
                );
              }
              return null;
            })()}
          </div>
        </div>

        {config.caseCard.showDescription && (
          <div className="flex-1 space-y-1">
            {(() => {
              if (isLoading) {
                return (
                  <>
                    <div className="h-3 w-full animate-pulse rounded bg-muted" />
                    <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-4/6 animate-pulse rounded bg-muted" />
                  </>
                );
              }
              if (error) {
                return <p className="text-xs text-destructive">Unable to load description</p>;
              }
              if (details) {
                return (
                  <>
                    {anamneseItem.descriptionList
                      .slice(0, config.caseCard.descriptionMaxLines)
                      .map((line: any, idx: any) => (
                        <p key={idx} className="line-clamp-1 text-sm text-muted-foreground">
                          {parseHtml(line)}
                        </p>
                      ))}
                    {anamneseItem.descriptionList.length > config.caseCard.descriptionMaxLines && (
                      <p className="text-sm text-muted-foreground">...</p>
                    )}
                  </>
                );
              }
              return null;
            })()}
          </div>
        )}

        {/* Read Only Toggle + Reset Button - Bottom right, above progress */}
        {hasAccessToReadOnlyMode && (
          <div
            className="mt-4 flex items-center justify-end gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <ReadOnlyToggle compact />
            {userId && <CertificationResetButton userId={userId} caseId={caseId} compact />}
          </div>
        )}

        {config.caseCard.showProgress && isStarted && !isReadOnly && (
          <div className="mt-2 border-t border-border pt-4">
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>{t('caseSelector.caseCard.progress')}</span>
              <span>{isCompleted ? '100%' : `${Math.round(progress)}%`}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-accent transition-all"
                style={{
                  width: isCompleted ? '100%' : `${progress}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Certification Button - Normal mode */}
        {config.display.showCertificationButton &&
          userId &&
          !isDemo &&
          !isReadOnly &&
          !isAccreditationUser && (
            <div className="mt-4" onClick={(e) => e.stopPropagation()}>
              <CertificationButton
                caseId={caseId}
                userId={userId}
                onStartCertification={() => onStartCertification(caseId)}
                onTestClose={() => {
                  // Force re-render to update button state after certification test
                }}
              />
            </div>
          )}

        {/* Certification Button - Read Only mode (includes accreditation users) */}
        {(isReadOnly || isAccreditationUser) && (
          <div className="mt-4" onClick={(e) => e.stopPropagation()}>
            <Button
              onClick={handleCertificationClick}
              className="w-full bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
            >
              {t('certification.button.viewCertification')}
            </Button>
          </div>
        )}
      </div>

      {/* Read Only Case Modal */}
      {showReadOnlyCase && (
        <div className="fixed inset-0 z-50 bg-background">
          <ReadOnlyCaseRenderer caseId={caseId} onClose={() => setShowReadOnlyCase(false)} />
        </div>
      )}

      {/* Read Only Certification Modal */}
      {showReadOnlyCertification && details && (
        <ReadOnlyCertification
          caseId={caseId}
          caseTitle={details.displayName}
          onClose={() => setShowReadOnlyCertification(false)}
        />
      )}
    </>
  );
};
